import { Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { APP_BASE_HREF } from '@angular/common';

import { Subscription, filter, NEVER } from 'rxjs';
import { catchError, switchMap, take, tap } from 'rxjs/operators';
import { MessageService } from 'primeng/api';

import { PartialMentionApiModel } from '../../../../../api/mentions/model/mention.model';
import { TwitterSubTypeEnum } from '../../../../Basic/enums/twitter-sub-type.enum';
import { MediaTypeEnum } from '../../../../Basic/enums/media-type.enum';

import { Mention } from '../../../../Mentions/models/mention';
import { TagsService } from '../../../../Tags/services/tags.service';
import { messageSeverityEnum } from '../../../../Misc/enums/message-severity.enum';
import { ButtonTypesEnum } from '../../../../Forms/enums/button-types.enum';
import { attributeTypesEnum } from '../../../../Misc/enums/attribute-types.enum';
import { AttributeModel } from '../../../../Misc/models/attribute.model';
import { EngagementAttributeTypesEnum } from '../../../../../Monitor/Shared/enums/engagement-attribute-types.enum';

import { PreferencesModel } from '../../../../../Core/Users/models/preferences.model';
import { CurrentUserStateService } from '../../../../../Core/Users/services/current-user-state.service';
import { LocalizationService } from '../../../../../Core/Localization/services/localization.service';
import { AccountModel } from '../../../../../Core/Users/models/account.model';

import { Folder } from '../../../../../Monitor/Shared/models/folder.model';

import { AgprComponentStyleEnum } from '../../../../Basic/enums/social-analysis-types.enum';
import { AbstractMentionsService } from '../../../services/abstract-mentions.service';

@Component({
    selector: 'agpr-social-post-details',
    templateUrl: './social-post-details.component.html',
    styleUrls: ['./social-post-details.component.scss'],
    standalone: false
})
export class SocialPostDetailsComponent implements OnInit, OnDestroy {

    @Input() mention: Mention;

    preferences: PreferencesModel;
    account: AccountModel;

    folders: Folder[];
    defaultFoldersToShow: number = 3;
    foldersToShow: number = this.defaultFoldersToShow;
    shownFolders: Folder[];

    mentionRegion: string;

    postInformationBadges: AttributeModel[] = [];
    engagementBadges: AttributeModel[] = [];

    usersMap: {[key: number]: string} = [];

    private initialTagIds: number[];
    private subscriptions: Subscription[] = [];

    readonly messageSeverityEnum = messageSeverityEnum;
    readonly buttonTypes = ButtonTypesEnum;
    readonly mediaTypeEnum = MediaTypeEnum;
    readonly agprComponentStyleEnum = AgprComponentStyleEnum;
    readonly tweetTypeEnum = TwitterSubTypeEnum;
    readonly attributeTypesEnum = attributeTypesEnum;

    constructor(
        protected currentUserStateService: CurrentUserStateService,
        @Inject('MentionsService') private mentionsService: AbstractMentionsService,
        @Inject(APP_BASE_HREF) public appBaseHref: string,
        private messageService: MessageService,
        private tagsService: TagsService,
        private localizationService: LocalizationService,
    ) {}

    ngOnInit(): void {
        this.initialTagIds = [...this.mention.tagIds];

        // TODO: do something similar to AttributeListComponent, but cleaner
        // Waiting to get all the data, to know all the different use cases
        this.subscriptions.push(
            this.currentUserStateService.getPreferences().pipe(filter(x => !!x)).subscribe(
                prefs => this.preferences = prefs
            ),

            this.currentUserStateService.getAccount().subscribe(
                account => this.account = account
            ),
            this.mentionsService.getMentionRegion(this.mention).subscribe((region) => {
                this.mentionRegion = 'N/A';
                if (region?.displayString) {
                    this.mentionRegion = region.displayString;
                }
                this.setPostInformationBadges();
            }),

            this.currentUserStateService.getAccountUsers().subscribe(
                users => {
                    this.usersMap = {};

                    if (users) {
                        users.forEach(user => {
                            this.usersMap[user.id] = user.getDisplayName();
                        });
                    }
                }
            ),
        );

        this.setPostInformationBadges();
        this.setEngagementBadges();
    }

    private setPostInformationBadges(): void {
        if (!this.mention) {
            this.postInformationBadges = [];
            return;
        }

        const followerLabel = this.mention?.mediaType?.id === MediaTypeEnum.YOUTUBE
            ? 'Social.post.viewer.subscribers'
            : 'Social.post.viewer.followers';

        this.postInformationBadges = [
            {
                id: 'socialMedia',
                label: 'Social.post.viewer.socialMedia',
                value: this.mention?.mediaType?.name,
                type: attributeTypesEnum.simple,
                icon: this.mention?.mediaType?.icon,
                mediaType: this.mention?.mediaType?.id
            },
            {
                id: 'sentiment',
                label: 'Social.post.viewer.sentiment',
                value: this.mention?.sentimentName,
                type: attributeTypesEnum.sentiment,
                icon: 'fa-solid fa-face-smile'
            },
            {
                id: 'followers',
                label: followerLabel,
                value: this.mention?.audienceReach,
                type: attributeTypesEnum.audienceReach,
                icon: 'fa-solid fa-user-group'
            },
            {
                id: 'language',
                label: 'Social.post.viewer.language',
                value: this.mention?.language,
                type: attributeTypesEnum.language,
                icon: 'fa-kit fa-agpr-language'
            },
            {
                id: 'location',
                label: 'Social.post.viewer.location',
                value: this.mentionRegion,
                type: attributeTypesEnum.simple,
                icon: 'fa-solid fa-location-dot'
            }
        ];
    }

    private setEngagementBadges(): void {
        if (!this.mention) {
            this.engagementBadges = [];
            return;
        }

        const isTwitter = this.mention?.mediaType?.id === MediaTypeEnum.TWITTER;
        const isBluesky = this.mention?.mediaType?.id === MediaTypeEnum.BLUESKY;
        const isLinkedIn = this.mention?.mediaType?.id === MediaTypeEnum.LINKEDIN;
        const isFacebook = this.mention?.mediaType?.id === MediaTypeEnum.FACEBOOK;
        const isTikTok = this.mention?.mediaType?.id === MediaTypeEnum.TIKTOK;
        const isYoutube = this.mention?.mediaType?.id === MediaTypeEnum.YOUTUBE;

        const likesLabel = isLinkedIn ? 'Social.post.viewer.reactions' : 'Social.post.viewer.likes';
        const commentsLabel = (isTwitter || isBluesky) ? 'Social.post.viewer.replies' : 'Social.post.viewer.comments';
        const likeIcon = (isFacebook || isTikTok || isYoutube || this.mention?.mediaType?.id === MediaTypeEnum.INSTAGRAM)
            ? 'fa-solid fa-thumbs-up'
            : 'fa-solid fa-heart';

        const engagementAttribute: AttributeModel = {
            id: EngagementAttributeTypesEnum.engagements,
            label: 'Social.post.viewer.engagements',
            value: this.mention.engagements,
            type: attributeTypesEnum.engagement,
            icon: 'fa-solid fa-retweet'
        };

        const engagementRateAttribute: AttributeModel = {
            id: EngagementAttributeTypesEnum.engagementRate,
            label: 'Social.post.viewer.engagementRate',
            value: this.mention.engagementRate,
            type: attributeTypesEnum.percent,
            icon: 'fa-solid fa-percent'
        };

        const likesAttribute: AttributeModel = {
            id: EngagementAttributeTypesEnum.likes,
            label: likesLabel,
            value: this.mention.like,
            type: attributeTypesEnum.engagement,
            icon: likeIcon
        };

        const commentsAttribute: AttributeModel = {
            id: EngagementAttributeTypesEnum.comments,
            label: commentsLabel,
            value: this.mention.reply,
            type: attributeTypesEnum.engagement,
            icon: 'fa-solid fa-comment'
        };

        const sharesAttribute: AttributeModel = {
            id: EngagementAttributeTypesEnum.shares,
            label: 'Social.post.viewer.shares',
            value: this.mention.share,
            type: attributeTypesEnum.engagement,
            icon: 'fa-solid fa-circle-nodes'
        };

        const retweetsAttribute: AttributeModel = {
            id: EngagementAttributeTypesEnum.shares,
            label: 'Social.post.viewer.retweets',
            value: this.mention.share,
            type: attributeTypesEnum.engagement,
            icon: 'fa-solid fa-arrows-retweet'
        };

        const viewsAttribute: AttributeModel = {
            id: EngagementAttributeTypesEnum.views,
            label: 'Social.post.viewer.views',
            value: this.mention.view,
            type: attributeTypesEnum.engagement,
            icon: 'fa-solid fa-eye'
        };

        const quotesAttribute: AttributeModel = {
            id: EngagementAttributeTypesEnum.quotes,
            label: 'Social.post.viewer.quotes',
            value: this.mention.quote,
            type: attributeTypesEnum.engagement,
            icon: 'fa-solid fa-quotes'
        };

        this.engagementBadges = [
            engagementAttribute,
            engagementRateAttribute,
            likesAttribute,
            commentsAttribute
        ];

        if (isTwitter || isBluesky) {
            this.engagementBadges.push(retweetsAttribute, quotesAttribute);
        } else if (isFacebook || isTikTok) {
            this.engagementBadges.push(sharesAttribute);
        }

        if (isYoutube || isTikTok) {
            this.engagementBadges.push(viewsAttribute);
        }
    }


    /**
     * Handles change to the tags selected for a mention
     */
    tagsUpdated(tagIds: number[]): void {
        this.mention.tagIds = tagIds;
        const cachedTagIds = [...this.initialTagIds];

        const patch: PartialMentionApiModel = {
            id: this.mention.mentionId,
            tagIds: this.mention.tagIds
        };

        this.mentionsService.patchAndReturnMention(patch).pipe(
            catchError(err => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: 'Monitor.tags.updateErrorHeading',
                    detail: 'Monitor.tags.updateErrorMessage'
                });

                return NEVER;
            }),
            switchMap((response) => {
                this.mentionsService.setFocusedMention(response);

                if (cachedTagIds.length < response.tagIds.length) {
                    const uncachedIds = response.tagIds.filter(id => !cachedTagIds.includes(id));
                    return this.tagsService.getTagsByIds(uncachedIds).pipe(
                        filter(x => !!x),
                        tap(tags => {
                            tags.forEach(tag => {
                                this.messageService.add({
                                    severity: messageSeverityEnum.success,
                                    summary: this.localizationService.transform('Monitor.tags.addedSuccessHeading', {
                                        tagName: tag.name
                                    }),
                                    detail: this.localizationService.transform('Monitor.tags.addedSuccessMessage', {
                                        tagName: tag.name
                                    })
                                });
                            });
                        }),
                        take(1)
                    );
                } else {
                    const unsavedTagIds = cachedTagIds.filter(id => !response.tagIds.includes(id));
                    return this.tagsService.getTagsByIds(unsavedTagIds).pipe(
                        filter(x => !!x),
                        tap(tags => {
                            tags.forEach(tag => {
                                this.messageService.add({
                                    severity: messageSeverityEnum.success,
                                    summary: this.localizationService.transform('Monitor.tags.removedSuccessHeading', {
                                        tagName: tag.name
                                    }),
                                    detail: this.localizationService.transform('Monitor.tags.removedSuccessMessage', {
                                        tagName: tag.name
                                    })
                                });
                            });
                        }),
                        take(1)
                    );
                }
            })
        ).subscribe();
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
